import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Download, Loader2, CheckCircle2, XCircle, FileText, Square, Trash2, OctagonX, ArrowUp, ArrowDown, Check, X, AlertTriangle } from 'lucide-react';
import {
  createProcessMapSession,
  getProcessMapSession,
  submitProcessMapBatch,
  downloadProcessMapDocument,
  stopProcessMapDocument,
  deleteProcessMapDocument,
  saveCorpusRule,
  applyCorpusRule,
  overrideChecklistItem,
} from './processMapSynthesisApi';
import { ProcessMapDocument, ProcessMapSession, ProcessMapDocumentStatus } from './types';
import WorkdayReferencePanel from './WorkdayReferencePanel';
import ProcessMapRevisionsPanel from './ProcessMapRevisionsPanel';

const MAX_FILES = 10;

const CHANGE_LOG: { date: string; summary: string }[] = [
  {
    date: 'Oct 28, 2026',
    summary: 'Every SOP uploaded on Revisions rewrites Definitions / Acronyms so Center of Excellence (and Centre / plural variants) reads Center of Expertise. The acronym COE stays. The rest of the SOP is unchanged.',
  },
  {
    date: 'Oct 21, 2026',
    summary: 'Revisions reads a returned SOP with redlines, comments, or other notes, proposes only those edits for approval (and lets you add a missed one), then writes just the approved changes into that same Word file. The download is the original name plus (Revised). Unaddressed first-pass comments and gap rows stay; addressed ones come out.',
  },
  {
    date: 'Oct 8, 2026',
    summary: 'A Workday reference tab checks a workshop deck against the SOPs already written in this session. You set the link. A proposal is written only when you approve it, and the Word file is rebuilt from that edit.',
  },
  {
    date: 'Sep 25, 2026',
    summary: 'Pacific Life glossary terms fill Key Definitions when they match the map. The second review retries once on timeout. Checklist rows give the reason they failed or passed, and Failed/Verify badges can be marked Passed after you address them.',
  },
  {
    date: 'Sep 24, 2026',
    summary: 'A refresh or a trip to another page keeps the session. Past sessions show in History. Uploading a file that is already running stays on that run instead of starting a second one.',
  },
  {
    date: 'Sep 24, 2026',
    summary: 'An admin can paste a client note. Jarvis saves it as one rule and applies only that rule to every Word file in a folder. Originals move to Archived - date. Updated files are named with the date. Comments in a file are listed for review and are not saved as rules.',
  },
  {
    date: 'Sep 24, 2026',
    summary: 'An arrow is used only when it touches a box. The second reviewer reports what it found and does not rewrite the SOP. A council meets only when there is a disagreement. Finished SOPs can be updated for formatting, or for gap-question wording, without drafting them again.',
  },
];
const POLL_INTERVAL_MS = 8000;
const MAX_POLL_MS = 60000;

function requestError(err: any, fallback: string): string {
  const server = err?.response?.data?.error;
  if (typeof server === 'string' && server.trim()) return server;
  return fallback;
}

const STATUS_LABEL: Record<ProcessMapDocumentStatus, string> = {
  queued: 'Queued',
  extracting: 'Reading map (Pass 1)',
  interpreting: 'Interpreting map (Pass 1b)',
  drafting: 'Drafting SOP (Pass 2)',
  verifying: 'Checking format (Pass 3A)',
  auditing: 'Checking the draft',
  council: 'Council review',
  parsing: 'Reading changes',
  review: 'Review changes',
  applying: 'Applying edits',
  complete: 'Complete',
  failed: 'Failed',
  stopped: 'Stopped',
};

const TERMINAL_STATUSES: ProcessMapDocumentStatus[] = ['complete', 'failed', 'stopped', 'review'];

const STATUS_SORT_ORDER: Record<ProcessMapDocumentStatus, number> = {
  queued: 0,
  extracting: 1,
  interpreting: 2,
  drafting: 3,
  verifying: 4,
  auditing: 5,
  council: 6,
  parsing: 3,
  applying: 4,
  review: 7,
  stopped: 8,
  failed: 9,
  complete: 10,
};

type SortField = 'fileName' | 'status' | 'createdAt';
type SortDir = 'asc' | 'desc';

function isTerminal(status: ProcessMapDocumentStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

function documentCreatedAt(doc: ProcessMapDocument): string | null {
  return doc.completedAt || doc.startedAt || null;
}

function formatCreatedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StatusBadge({ doc }: { doc: ProcessMapDocument }) {
  if (doc.status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-xs font-medium" title={doc.verifiedCleanReason || undefined}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Complete
        {doc.verifiedClean === true && (
          <span className="text-emerald-800 ml-1">(verified)</span>
        )}
        {doc.verifiedClean === false && (
          <span className="text-amber-700 ml-1" title={doc.verifiedCleanReason || undefined}>(residual findings — see notes)</span>
        )}
      </span>
    );
  }
  if (doc.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-xs font-medium" title={doc.error || ''}>
        <XCircle className="w-3.5 h-3.5" /> Failed
      </span>
    );
  }
  if (doc.status === 'stopped') {
    return (
      <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-50 border border-slate-300 rounded-full px-2 py-0.5 text-xs font-medium">
        <OctagonX className="w-3.5 h-3.5" /> Stopped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-xs font-medium">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {STATUS_LABEL[doc.status] || doc.status}
    </span>
  );
}

interface DeletePromptState {
  doc: ProcessMapDocument;
  wasRunning: boolean;
}

interface ProcessMapSynthesisWorkspaceProps {
  loadSessionId?: number | null;
  onSessionChange?: (id: number | null) => void;
}

export default function ProcessMapSynthesisWorkspace({ loadSessionId, onSessionChange }: ProcessMapSynthesisWorkspaceProps) {
  const [session, setSession] = useState<ProcessMapSession | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [deletePrompt, setDeletePrompt] = useState<DeletePromptState | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [screen, setScreen] = useState<'write' | 'update' | 'reference' | 'revisions'>('write');
  const [openNotesId, setOpenNotesId] = useState<string | null>(null);
  const [clientNote, setClientNote] = useState('');
  const [folderUrl, setFolderUrl] = useState('');
  const [savedRule, setSavedRule] = useState<{ instruction: string; patches: { op: string; role?: string; text?: string; afterHeading?: string; color?: string }[] } | null>(null);
  const [corpusReports, setCorpusReports] = useState<{ fileName: string; status: string; archivedAs?: string; commentsForReview: string[]; checks: { label: string; status: string }[]; results: { detail: string; status: string }[] }[] | null>(null);
  const [updating, setUpdating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [checklistBusy, setChecklistBusy] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollGen = useRef(0);

  const stopPolling = useCallback(() => {
    pollGen.current += 1;
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const startPolling = useCallback((sessionId: number) => {
    stopPolling();
    const generation = pollGen.current;
    let delay = POLL_INTERVAL_MS;
    const tick = async () => {
      if (pollGen.current !== generation) return;
      try {
        const refreshed = await getProcessMapSession(sessionId);
        if (pollGen.current !== generation) return;
        setSession(refreshed);
        setError((prev) => (prev && prev.startsWith('This session is still here') ? null : prev));
        const stillRunning = (refreshed.documents || []).some((d) => !isTerminal(d.status));
        if (!stillRunning) {
          stopPolling();
          return;
        }
        delay = POLL_INTERVAL_MS;
      } catch (err: any) {
        if (pollGen.current !== generation) return;
        if (err?.response?.status === 429) {
          delay = Math.min(delay * 2, MAX_POLL_MS);
          setError('This session is still here. The page will check again in a moment.');
        }
      }
      if (pollGen.current === generation) {
        pollTimer.current = setTimeout(tick, delay);
      }
    };
    pollTimer.current = setTimeout(tick, delay);
  }, [stopPolling]);

  const ensurePollingIfNeeded = useCallback((sess: ProcessMapSession) => {
    const stillRunning = (sess.documents || []).some((d) => !isTerminal(d.status));
    if (stillRunning) startPolling(sess.id); else stopPolling();
  }, [startPolling, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Load the session this tile was opened with (mirrors GFDIntakeWorkspace's
  // loadSessionId prop pattern — driven by the top bar's "New Session" /
  // session-history controls, not an in-panel session picker).
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!loadSessionId) {
      setSession(null);
      setRestoring(false);
      setError(null);
      stopPolling();
      return;
    }
    let delay = POLL_INTERVAL_MS;
    stopPolling();
    setSession((current) => (current && Number(current.id) === Number(loadSessionId) ? current : null));
    const attempt = async () => {
      setRestoring(true);
      try {
        const loaded = await getProcessMapSession(loadSessionId);
        if (cancelled) return;
        setError(null);
        setRestoring(false);
        setSession(loaded);
        const docs = loaded.documents || [];
        if (docs.length && docs.every((doc) => doc.kind === 'revision')) {
          setScreen('revisions');
        }
        ensurePollingIfNeeded(loaded);
      } catch (err: any) {
        if (cancelled) return;
        setError(requestError(err, 'This session could not be loaded. It will be tried again.'));
        if (err?.response?.status === 429) delay = Math.min(delay * 2, MAX_POLL_MS);
        retryTimer = setTimeout(() => { if (!cancelled) attempt(); }, delay);
      }
    };
    attempt();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [loadSessionId, ensurePollingIfNeeded, stopPolling]);

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    const pdfs = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith('.pdf')).slice(0, MAX_FILES);
    if (!pdfs.length) {
      setError('Only PDF files are accepted.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      let sess = session;
      if (!sess) {
        sess = await createProcessMapSession('Process Map Synthesis');
        setSession(sess);
        onSessionChange?.(sess.id);
      }
      const { session: updated, skipped } = await submitProcessMapBatch(sess.id, pdfs);
      setSession(updated);
      if (skipped.length === 1) {
        setError(`${skipped[0]} is already being processed. It is still in this session.`);
      } else if (skipped.length > 1) {
        setError(`${skipped.join(', ')} are already being processed. They are still in this session.`);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      setError(requestError(err, 'Failed to start synthesis'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: ProcessMapDocument) => {
    if (!session) return;
    try {
      const fallback = doc.kind === 'revision'
        ? `${doc.fileName.replace(/\.[^.]+$/, '').replace(/\s*\(Revised\)$/i, '')} (Revised).docx`
        : `${doc.fileName.replace(/\.pdf$/i, '')}-SOP.docx`;
      await downloadProcessMapDocument(session.id, doc.id, fallback);
    } catch (err: any) {
      setError(requestError(err, 'Download failed'));
    }
  };

  const handleStop = async (doc: ProcessMapDocument) => {
    if (!session) return;
    setPendingAction(`stop:${doc.id}`);
    setError(null);
    try {
      const updated = await stopProcessMapDocument(session.id, doc.id);
      setSession(updated);
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      setError(requestError(err, 'Stop failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const openDeletePrompt = (doc: ProcessMapDocument) => {
    setDeletePrompt({ doc, wasRunning: !isTerminal(doc.status) });
  };

  const confirmDelete = async () => {
    if (!session || !deletePrompt) return;
    const { doc } = deletePrompt;
    setPendingAction(`delete:${doc.id}`);
    setError(null);
    try {
      const updated = await deleteProcessMapDocument(session.id, doc.id);
      setSession(updated);
      ensurePollingIfNeeded(updated);
      setDeletePrompt(null);
    } catch (err: any) {
      setError(requestError(err, 'Delete failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const documents = (session?.documents || []).filter((doc) => doc.kind !== 'revision');
  const referenceSops = useMemo(() => (
    documents
      .filter((doc) => doc.status === 'complete' && doc.correctedSop)
      .map((doc) => ({
        id: String(doc.id),
        sourceFileName: doc.fileName || '',
        runAt: doc.completedAt || doc.startedAt || null,
        sop: doc.correctedSop as Record<string, unknown>,
      }))
  ), [documents]);
  const sortedDocuments = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...documents].sort((a, b) => {
      if (sortField === 'fileName') {
        return dir * (a.fileName || '').localeCompare(b.fileName || '', undefined, { sensitivity: 'base' });
      }
      if (sortField === 'status') {
        return dir * ((STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99));
      }
      const aTime = documentCreatedAt(a) ? new Date(documentCreatedAt(a) as string).getTime() : 0;
      const bTime = documentCreatedAt(b) ? new Date(documentCreatedAt(b) as string).getTime() : 0;
      return dir * (aTime - bTime);
    });
  }, [documents, sortField, sortDir]);

  const SortHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: 'left' | 'right' }) => {
    const active = sortField === field;
    return (
      <th className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-2 font-medium`}>
        <button
          type="button"
          onClick={() => handleSort(field)}
          className={`inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-800 ${active ? 'text-gray-800' : ''} ${align === 'right' ? 'ml-auto' : ''}`}
          title={field === 'createdAt' ? 'Sort by created time' : `Sort by ${label}`}
        >
          {label}
          {active ? (
            sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          ) : (
            <span className="w-3 h-3" />
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="min-h-0 flex-1 flex flex-col gap-4">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Process Map Synthesis</h2>
          <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            Temporary module
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          {screen === 'write'
            ? 'Upload a process map PDF. Jarvis writes a standard operating procedure in Word. Open Notes to see what was corrected, what the council decided, and what a person still needs to confirm. You can download the Word file even when something still needs a look.'
            : screen === 'reference'
              ? 'Upload the three Pacific Life workshop decks. Jarvis checks each deck against the SOPs already written in this session. You set the link from the file, the Workday process, and the condition to an SOP title. Nothing is written until you approve a proposal.'
              : screen === 'revisions'
                ? 'Upload the SOP the client sent back. Choose Redlines, Comments, or Other (Other lets you attach notes files). Update proposes only those edits. Approve, add anything that was missed, then apply. Nothing else in the Word file changes.'
                : 'Paste the client note, save the rule, and point Jarvis at the SharePoint folder. It edits only that rule. Originals are archived. Comments in a file are listed for a person and are not applied to the other files.'}
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={() => setScreen('write')} className={`px-3 py-1 rounded-full text-sm font-medium border ${screen === 'write' ? 'bg-super-dark-purple text-white border-super-dark-purple' : 'bg-white text-gray-600 border-gray-300'}`}>Writing a new SOP</button>
          <button type="button" onClick={() => setScreen('update')} className={`px-3 py-1 rounded-full text-sm font-medium border ${screen === 'update' ? 'bg-super-dark-purple text-white border-super-dark-purple' : 'bg-white text-gray-600 border-gray-300'}`}>Updating existing documents</button>
          <button type="button" onClick={() => setScreen('reference')} className={`px-3 py-1 rounded-full text-sm font-medium border ${screen === 'reference' ? 'bg-super-dark-purple text-white border-super-dark-purple' : 'bg-white text-gray-600 border-gray-300'}`}>Workday reference</button>
          <button type="button" onClick={() => setScreen('revisions')} className={`px-3 py-1 rounded-full text-sm font-medium border ${screen === 'revisions' ? 'bg-super-dark-purple text-white border-super-dark-purple' : 'bg-white text-gray-600 border-gray-300'}`}>Revisions</button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
      )}

      {screen === 'update' && (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <h3 className="text-sm font-semibold text-gray-900">Client note</h3>
            <textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              placeholder='Example: Set step headings to #2E75B6. Set Geographic Scope to "The process applies to the United States."'
              className="mt-3 w-full h-28 text-sm border border-gray-200 rounded-md p-2"
            />
            <button
              type="button"
              disabled={updating || !clientNote.trim()}
              onClick={async () => {
                setUpdating(true);
                setError(null);
                try {
                  const rule = await saveCorpusRule(clientNote.trim());
                  setSavedRule(rule);
                } catch (err: any) {
                  setError(requestError(err, 'The note did not become a rule.'));
                } finally {
                  setUpdating(false);
                }
              }}
              className="mt-3 w-full px-3 py-1.5 text-sm font-medium text-white bg-super-dark-purple rounded-md disabled:opacity-50"
            >
              Save this rule
            </button>
            {savedRule && (
              <p className="mt-3 text-xs text-gray-600">Saved. New SOPs use this rule too. {savedRule.patches.map((patch) => patch.op === 'set_style' ? `${patch.role} color ${patch.color}` : `${patch.afterHeading}: ${patch.text}`).join(' ')}</p>
            )}
            <label className="block text-sm font-medium text-gray-800 mt-4">SharePoint folder</label>
            <input
              value={folderUrl}
              onChange={(e) => setFolderUrl(e.target.value)}
              placeholder="https://….sharepoint.com/… or a server folder"
              className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
            />
            <button
              type="button"
              disabled={updating || !savedRule || !folderUrl.trim()}
              onClick={async () => {
                setUpdating(true);
                setError(null);
                try {
                  const result = await applyCorpusRule(folderUrl.trim());
                  setCorpusReports(result.reports);
                } catch (err: any) {
                  setError(requestError(err, 'The folder update failed.'));
                } finally {
                  setUpdating(false);
                }
              }}
              className="mt-3 w-full px-3 py-1.5 text-sm font-medium text-white bg-electric-blue rounded-md disabled:opacity-50"
            >
              {updating ? 'Updating…' : 'Update the folder'}
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wide">Each document</div>
            {!corpusReports ? (
              <p className="px-4 py-6 text-sm text-gray-500">Save a rule, then point at the folder. Originals move to an Archived folder. Updated files keep the document name plus the date.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-4 py-2">Document</th><th className="text-left px-4 py-2">Checks</th><th className="text-left px-4 py-2">Comments to review</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {corpusReports.map((row) => (
                    <tr key={row.fileName}>
                      <td className="px-4 py-2.5 text-gray-800">{row.fileName}{row.archivedAs ? <div className="text-xs text-gray-500">Previous file: {row.archivedAs}</div> : null}</td>
                      <td className="px-4 py-2.5 text-gray-700">{(row.checks || []).map((check) => `${check.label}: ${check.status}`).join('. ')} {(row.results || []).map((item) => item.detail).join(' ')}</td>
                      <td className="px-4 py-2.5 text-gray-700">{(row.commentsForReview || []).length ? row.commentsForReview.join(' ') : 'None'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {screen === 'reference' && (
        <WorkdayReferencePanel
          processSessionId={session?.id ?? null}
          sops={referenceSops}
          onError={setError}
        />
      )}

      {screen === 'revisions' && (
        <ProcessMapRevisionsPanel
          session={session}
          restoring={restoring}
          onSession={setSession}
          onSessionChange={onSessionChange}
          onError={setError}
          onDelete={openDeletePrompt}
          ensurePollingIfNeeded={ensurePollingIfNeeded}
        />
      )}

      {screen === 'write' && <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Documents</span>
          {/* Always available — never disabled while other documents are
              still processing, so a second (or third, fourth…) upload can
              be added to this same session at any time. Uploads run
              concurrently in the backend; this button only blocks on the
              instant of the POST itself, not on the pipeline finishing. */}
          <label
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white ${uploading ? 'bg-gray-300 cursor-wait' : 'bg-electric-blue cursor-pointer hover:opacity-90'} transition-opacity`}
            title={`Upload PDF${documents.length ? 's' : ''} (up to ${MAX_FILES} at a time, run concurrently)`}
          >
            <span>Upload</span>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              className="hidden"
              disabled={uploading}
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
          </label>
        </div>
        {documents.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">
            {restoring
              ? 'Loading this session…'
              : 'No documents yet — click + to upload a Process Map PDF.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <SortHeader field="fileName" label="Document" />
                <SortHeader field="status" label="Status" />
                <SortHeader field="createdAt" label="Final SOP" align="right" />
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedDocuments.map((doc) => {
                const terminal = isTerminal(doc.status);
                const stopBusy = pendingAction === `stop:${doc.id}`;
                const deleteBusy = pendingAction === `delete:${doc.id}`;
                const createdLabel = formatCreatedAt(documentCreatedAt(doc));
                const notes = doc.reviewNotes || [];
                const checklist = doc.reviewChecklist || [];
                const hint = notes.find((n) => n.tone === 'needs-person')
                  ? 'Download is ready. Something still needs a person to confirm.'
                  : notes.length
                    ? 'Download is ready. Open notes for what changed.'
                    : null;
                return (
                  <React.Fragment key={doc.id}>
                  <tr>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-gray-800 truncate">{doc.fileName}</span>
                      </div>
                      {doc.status === 'failed' && doc.error && (
                        <div className="text-xs text-red-600 mt-0.5 ml-6">{doc.error}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge doc={doc} />
                      {doc.status === 'complete' && hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
                      {(notes.length > 0 || checklist.length > 0) && (
                        <button type="button" onClick={() => setOpenNotesId(openNotesId === doc.id ? null : doc.id)} className="block text-xs font-medium text-electric-blue mt-1 hover:underline">
                          {openNotesId === doc.id ? 'Hide notes' : 'Notes'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {doc.status === 'complete' ? (
                        <div className="inline-flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleDownload(doc)}
                            className="inline-flex items-center gap-1 text-electric-blue hover:underline text-sm font-medium"
                          >
                            <Download className="w-3.5 h-3.5" /> Final.docx
                          </button>
                          {createdLabel && (
                            <span className="text-xs text-gray-500 whitespace-nowrap" title={doc.completedAt || doc.startedAt || undefined}>
                              {createdLabel}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {!terminal && (
                          <button
                            type="button"
                            onClick={() => handleStop(doc)}
                            disabled={stopBusy}
                            title="Stop this run"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {stopBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                            Stop
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openDeletePrompt(doc)}
                          disabled={deleteBusy}
                          title="Delete this document and any generated SOP"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {openNotesId === doc.id && (notes.length > 0 || checklist.length > 0) && (
                    <tr>
                      <td colSpan={4} className="px-4 pb-3 bg-gray-50">
                        {checklist.length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-md p-3 mb-2">
                            <div className="text-sm font-medium text-gray-900">Checklist</div>
                            <p className="text-xs text-gray-500 mt-1">Click a Failed or Verify badge to mark it Passed after you address it.</p>
                            {doc.verifiedCleanReason && (
                              <p className={`text-xs mt-2 ${doc.verifiedClean ? 'text-emerald-700' : 'text-amber-700'}`}>
                                {doc.verifiedClean ? 'Verified: ' : 'Not fully verified: '}{doc.verifiedCleanReason}
                              </p>
                            )}
                            {['Map logic', 'Formatting', 'Client checklist'].map((group) => {
                              const rows = checklist
                                .map((item, index) => ({ item, index }))
                                .filter(({ item }) => (item.group || 'Map logic') === group);
                              if (!rows.length) return null;
                              return (
                                <div key={group} className="mt-3">
                                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group}</div>
                                  <ul className="mt-2 space-y-1.5">
                                    {rows.map(({ item, index }) => {
                                      const badge = item.status === 'failed'
                                        ? { label: 'Failed', className: 'text-red-700 bg-red-50 border-red-200', icon: <X className="w-3.5 h-3.5" /> }
                                        : item.status === 'verify'
                                          ? { label: 'Verify', className: 'text-amber-700 bg-amber-50 border-amber-200', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
                                          : { label: 'Passed', className: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: <Check className="w-3.5 h-3.5" /> };
                                      const canOverride = item.status === 'failed' || item.status === 'verify';
                                      const busyKey = `${doc.id}:${index}`;
                                      const busy = checklistBusy === busyKey;
                                      return (
                                        <li key={`${group}-${index}`} className="text-sm text-gray-700 flex items-start gap-2">
                                          {canOverride ? (
                                            <button
                                              type="button"
                                              disabled={busy || !session}
                                              title="Mark as addressed (Passed)"
                                              onClick={async () => {
                                                if (!session) return;
                                                setChecklistBusy(busyKey);
                                                setError(null);
                                                try {
                                                  const refreshed = await overrideChecklistItem(session.id, doc.id, index, 'passed');
                                                  setSession(refreshed);
                                                } catch (err: any) {
                                                  setError(requestError(err, 'Could not mark that checklist item as passed.'));
                                                } finally {
                                                  setChecklistBusy(null);
                                                }
                                              }}
                                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border shrink-0 cursor-pointer hover:opacity-80 disabled:opacity-50 ${badge.className}`}
                                            >
                                              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : badge.icon}
                                              {badge.label}
                                            </button>
                                          ) : (
                                            <span title={item.overridden ? 'Marked addressed by reviewer' : badge.label} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border shrink-0 ${badge.className}`}>
                                              {badge.icon}
                                              {badge.label}
                                            </span>
                                          )}
                                          <span>
                                            {item.label}
                                            {item.detail ? <span className="block text-xs text-gray-500 mt-0.5">{item.detail}</span> : null}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="grid gap-2 md:grid-cols-2">
                          {notes.map((note, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-md p-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium border ${note.tone === 'needs-person' ? 'text-amber-700 bg-amber-50 border-amber-200' : note.tone === 'fixed' || note.tone === 'ok' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                                {note.tone === 'needs-person' ? 'Needs a person' : note.tone === 'fixed' ? 'Fixed in the Word file' : note.tone === 'ok' ? 'Okay' : 'Note'}
                              </span>
                              <div className="text-sm font-medium text-gray-900 mt-2">{note.title}</div>
                              <p className="text-sm text-gray-600 mt-1">{note.body}</p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wide">Change log</div>
        <ul className="divide-y divide-gray-100">
          {CHANGE_LOG.map((entry) => (
            <li key={entry.date + entry.summary} className="px-4 py-3">
              <div className="text-xs font-medium text-gray-500">{entry.date}</div>
              <p className="text-sm text-gray-800 mt-0.5">{entry.summary}</p>
            </li>
          ))}
        </ul>
      </div>

      {deletePrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => {
            // Click on the backdrop (but not on the panel) closes the prompt.
            if (e.target === e.currentTarget && pendingAction !== `delete:${deletePrompt.doc.id}`) {
              setDeletePrompt(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-red-600"><Trash2 className="w-5 h-5" /></div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">Delete this document?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-gray-800">{deletePrompt.doc.fileName}</span>
                  {deletePrompt.doc.status === 'complete'
                    ? ' and its generated SOP will be permanently removed.'
                    : ' will be permanently removed.'}
                  {deletePrompt.wasRunning && (
                    <> The run is still in progress and will be stopped first.</>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-2">This can't be undone.</p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeletePrompt(null)}
                disabled={pendingAction === `delete:${deletePrompt.doc.id}`}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={pendingAction === `delete:${deletePrompt.doc.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {pendingAction === `delete:${deletePrompt.doc.id}` ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
